import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import {
    ECredentialingFlowMode,
    StatusBucket,
    EUserPlatformStatus,
    ECoverStatesMode,
    EPracticeType,
    ENPIType,
    ECredentialingStep,
} from 'types/credentialing';
import type {
    PanelStatus,
    TCredentialingActionItem,
    TCredentialingContractView,
    TCredentialingOverview,
} from 'types/credentialing';
import ProviderStore from 'store/Provider';
import useCredentialInfo from 'hooks/credentialing/useCredentialInfo';
import useCredentialingPanels from 'hooks/credentialing/useCredentialingPanels';
import usePracticePanels from 'hooks/credentialing/usePracticePanels';
import useUpcomingPayers from 'hooks/credentialing/useUpcomingPayers';
import useActionNeeded from 'hooks/credentialing/useActionNeeded';
import useContractBanner from 'hooks/credentialing/useContractBanner';
import useCredentialingContracts from 'hooks/credentialing/useCredentialingContracts';
import useGroupActionNeeded from 'hooks/credentialing/useGroupActionNeeded';
import usePanelSummary from 'hooks/credentialing/usePanelSummary';
import usePracticePanelSummary from 'hooks/credentialing/usePracticePanelSummary';
import SuggestPayerModal from 'components/CredentialingModal/components/SuggestPayerModal';
import CredentialingModal from 'components/CredentialingModal';
import CredentialingInfoModal from 'pages/PPACredentialingStartPage/components/CredentialingInfoModal';
import { buildActionFocus, focusTab, type TActionFocus } from 'pages/PPACredentialingStartPage/actionFocus';
import { fetchProviderCredValuesByPanel } from 'pages/PPACredentialingStartPage/credReviewData';
import { BUCKET_ORDER, mapApiPanelRow } from './statusMeta';
import { isDelegatedGroupProvider, isReadOnlyGroupProvider } from './groupProviderAccess';
import { SvgEdit, SvgPlus } from './components/SvgIcons';
import SummaryCard from './components/SummaryCard';
import ActionNeededBanner from './components/ActionNeededBanner';
// import GrowthBanner from './components/GrowthBanner';
import SpeedUpModule from './components/SpeedUpModule';
import ProviderBar from './components/ProviderBar';
import GroupCredentialingRecordTable from './components/GroupCredentialingRecordTable';
import GroupActionNeededBanner from './components/GroupActionNeededBanner';
import SoloCredentialingRecordTable from './components/SoloCredentialingRecordTable';
import WithdrawPayerModal from './components/WithdrawPayerModal';
import ResubmitPayerModal from './components/ResubmitPayerModal';
import SetupReminderBanner from './components/SetupReminderBanner';
import MalpracticeExpiryBanner from './components/MalpracticeExpiryBanner';
import SignatureBackfillBanner from './components/SignatureBackfillBanner';
import SignContractsBanner from './components/SignContractsBanner';
import NotReceivedBanner from './components/NotReceivedBanner';
import CountersignedBanner from './components/CountersignedBanner';
import ExpiredBanner from './components/ExpiredBanner';
import s from './s.module.less';
import { PATH } from 'constants/path';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message, Spin } from 'antd';
import {
    markContractSigned,
    withdrawCredentialingPanel,
    addSoloProviderPayers,
    addGroupProviderPayersActive,
    getCredentialingOverview,
} from 'api/credentialing';
import PPACredentialingLandingPage from 'pages/PPACredentialingLandingPage';
import EditPaymentModal from 'components/EditPaymentModal';
import useCurrentUser from 'hooks/useCurrentUser';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { EFeatureType } from 'types/provider';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import type { SettingEnum } from 'types/enumerationData';
import { getHelloKlarityHost, isGroupAdmin } from 'utils/common';
import useCredSetupStatus from 'hooks/credentialing/useCredSetupStatus';
import CredentialingPayoutModal from 'components/CredentialingPayoutForm';

type OwnerTab = 'practice' | 'my';

// Message link from Product & Engineering 这里Flash不显示任何banner;

// Priority显示当前prototype里的banner, UI一样, 但麻烦把文字改成:
// Title: Want to get credentialed sooner?
// Body: You're already on the Klarity marketplace. Set Klarity Meet as your default in Calendar to reach the fastest credentialing queue and get credentialed sooner.

// CTA: Set Klarity Meet as default (点击去: https://provider.helloklarity.com/dashboard/calendar-setting?tab=2)

// Standard显示当前prototype里的banner.

// 这是当前第 4 页 PPT（QUEUE SEQUENCING · How Klarity sequences credentialing）的内容整理：
// 主题
// Klarity 如何为医疗人员（provider）排序做资质认证（credentialing），即"谁先被处理"的判定标准。分为面向 Providers（对外） 和 Internal Team（对内） 两栏，共分四个层级（Tier）。
// Tier 1 — Flash（最优先）
// 需同时满足三个条件：1）已在 marketplace 上架；2）默认使用 Klarity Telehealth；3）为 Prolisting 用户。三者都要满足（AND）。同一层级内，越早提交的越先处理。
// Tier 2 — Priority（次优先）
// 条件为：1）已在 marketplace 上架，且 2）默认使用 Klarity Telehealth，或（OR） 3）为 Prolisting 用户。相比 Tier 1，条件更宽松（满足部分即可）。同层级内早提交者优先。
// Tier 3 — Standard（标准）
// 已注册但尚未上架 marketplace 的 providers。同层级内早提交者优先。
// Tier 4 — Exception（例外）
// 适用对象：转介绍（referral）、战略性州（strategic state），或高患者需求的专科。需经领导层审批（Leadership approval）。

interface ProviderInfo {
    providerId: number;
    name: string;
    credentials: string;
    owner: boolean;
}

// 值为 CredentialingInfoModal 的 tab key：向导 7 步用 CredStepKey，另有 V2 新增 tab（payer-info 等）
const ACTION_STEP_TO_TAB: Record<string, string> = {
    BASIC_INFO: 'identity',
    LICENSES: 'licenses',
    PRACTICE: 'practice',
    MALPRACTICE: 'malpractice',
    BACKGROUND: 'experience',
    REFERENCES: 'references',
    VERIFICATION: 'final',
    PAYER_REQUIREMENTS: 'payer-info',
};

const CredentialingPageContent = () => {
    const [getUser] = ProviderStore.useStore();
    const currentProviderId = getUser('data')?.id;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [credentialInfo, credentialInfoLoading, refetchCredentialInfo] = useCredentialInfo();

    const isGroup = useMemo(() => {
        const npiValue = credentialInfo.billingNpiType?.value;
        return npiValue === ENPIType.PRACTICE_NPI;
    }, [credentialInfo.billingNpiType]);

    // Team provider（group 非 owner/manager）只读门控：隐藏 Settings/Add/owner tabs/行操作，
    // 仅展示自己的 my-credentialing 视图（panel/list 按 token 只返回本人 panel）。
    const [currentUser] = useCurrentUser();
    const isTeamProvider = !!currentUser?.groupPractice && !isGroupAdmin(currentUser);
    const isAdminView = !isTeamProvider;
    const isProviderReadOnly = isReadOnlyGroupProvider(currentUser);
    const loadPractice = isGroup && !isProviderReadOnly;

    const [panelRows, , refetchPanels] = useCredentialingPanels();
    const [practiceGroups, , refetchPracticeGroups] = usePracticePanels(loadPractice);
    const [upcomingPayers] = useUpcomingPayers(credentialInfo.states);
    const [actionNeeded, , refetchActionNeeded] = useActionNeeded();
    const contractView: TCredentialingContractView = loadPractice ? 'practice' : 'provider';
    const [contractBanners, , refetchContractBanner] = useContractBanner(contractView);
    const [contracts, , refetchContracts] = useCredentialingContracts(contractView);
    const [groupActionNeeded, , refetchGroupActionNeeded] = useGroupActionNeeded(loadPractice);
    const [panelSummary, , refetchPanelSummary] = usePanelSummary();
    const [practicePanelSummary, , refetchPracticePanelSummary] = usePracticePanelSummary(loadPractice);
    const [providerInfo] = useProviderHomeInfo();
    const feeWaived = !!providerInfo?.features?.includes(EFeatureType.CREDENTIALING_FEE_WAIVED);
    const [stateList] = useGetSettingData(EnumFields.STATE);

    const credInfoLoadedRef = useRef(false);
    if (credentialInfoLoading) credInfoLoadedRef.current = true;

    const userPlatformStatus = useMemo(() => {
        const tier = credentialInfo.slaTier?.toUpperCase();
        if (tier === 'FLASH') return EUserPlatformStatus.FLASH;
        if (tier === 'PRIORITY') return EUserPlatformStatus.PRIORITY;
        return EUserPlatformStatus.STANDARD;
    }, [credentialInfo.slaTier]);

    const practiceType = isGroup ? EPracticeType.GROUP : EPracticeType.SOLO;

    const coverStatesMode = useMemo(() => {
        const { states } = credentialInfo;
        if (states.length === 0) return ECoverStatesMode.NOCOVERAGE;
        if (states.length === 1) return ECoverStatesMode.SINGLE;
        return ECoverStatesMode.MULTI;
    }, [credentialInfo]);

    const panels: PanelStatus[] = useMemo(() => panelRows.map(mapApiPanelRow), [panelRows]);

    const uniqueProviders: ProviderInfo[] = useMemo(() => {
        const seen = new Set<number>();
        return credentialInfo.providers
            .filter((p) => {
                if (seen.has(p.providerId)) return false;
                seen.add(p.providerId);
                return true;
            })
            .map((p) => ({
                providerId: p.providerId,
                name: `${p.firstName} ${p.lastName}`,
                credentials: '',
                owner: p.providerId === currentProviderId,
            }));
    }, [credentialInfo.providers, currentProviderId]);

    const providerPanelsMap = useMemo(() => {
        const result: Record<number, PanelStatus[]> = {};
        panelRows.forEach((row) => {
            if (!result[row.providerId]) result[row.providerId] = [];
            result[row.providerId].push(mapApiPanelRow(row));
        });
        return result;
    }, [panelRows]);

    // 有「进行中」payer 的 provider（statusBucket 为 ACTION_NEEDED / IN_PROGRESS）。
    // 编辑 credentialing 信息保存前，据此决定是否弹「Update your credentialing info?」确认框。
    // 数据源与页面一致：solo 用 useCredentialingPanels 的 panelRows；group 用 usePracticePanels 的 practiceGroups。
    const inProgressProviderIds = useMemo(() => {
        const ids = new Set<number>();
        if (isGroup) {
            practiceGroups.forEach((g) => {
                if (g.actionNeeded > 0 || g.inProgress > 0) ids.add(g.providerId);
            });
        } else {
            panelRows.forEach((row) => {
                if (row.statusBucket === 'ACTION_NEEDED' || row.statusBucket === 'IN_PROGRESS') {
                    ids.add(row.providerId);
                }
            });
        }
        return ids;
    }, [isGroup, practiceGroups, panelRows]);

    const [activeState, setActiveState] = useState<string | null>(null);
    const [statusFilters, setStatusFilters] = useState<StatusBucket[]>([]);
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [addPayersOpen, setAddPayersOpen] = useState(false);
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const [editInfoOpen, setEditInfoOpen] = useState(false);
    const [editInfoValues, setEditInfoValues] = useState<Record<string, any> | undefined>(undefined);
    const [editInfoTab, setEditInfoTab] = useState<string | undefined>(undefined);
    const [editInfoProviderId, setEditInfoProviderId] = useState<number | undefined>(undefined);
    // action item 的字段级定位载荷（ctaPayload 协议），随弹窗关闭清空
    const [editInfoFocus, setEditInfoFocus] = useState<TActionFocus | null>(null);

    const [ownerTab, setOwnerTab] = useState<OwnerTab>('practice');

    // Withdraw/Resubmit（V2）：撤回走真接口后 refetch；Resubmit 复用 Add payers 流程
    const [withdrawTarget, setWithdrawTarget] = useState<PanelStatus | null>(null);
    const [withdrawing, setWithdrawing] = useState(false);

    const handleWithdrawConfirm = async () => {
        if (!withdrawTarget?.klarityPayerId) return;
        setWithdrawing(true);
        try {
            const res: any = await withdrawCredentialingPanel(withdrawTarget.klarityPayerId, withdrawTarget.providerId);
            if (res?.error) {
                message.error(res.error || 'Failed to withdraw');
                return;
            }
            message.success(`${withdrawTarget.name} withdrawn.`);
            setWithdrawTarget(null);
            refetchPanels();
            refetchPanelSummary();
            refetchActionNeeded();
            if (isGroup) {
                refetchPracticeGroups();
                refetchPracticePanelSummary();
                refetchGroupActionNeeded();
            }
        } finally {
            setWithdrawing(false);
        }
    };

    // Resubmit：先弹确认（重新收押金），确认后直接调 add-payers 接口重新发起该 payer，不再走 Add payers 弹窗
    const [resubmitTarget, setResubmitTarget] = useState<PanelStatus | null>(null);
    const [resubmitting, setResubmitting] = useState(false);
    const handleResubmitConfirm = async () => {
        const row = resubmitTarget;
        if (!row?.klarityPayerId || resubmitting) return;
        setResubmitting(true);
        try {
            const res: any =
                isGroup && row.providerId
                    ? await addGroupProviderPayersActive([
                          { providerId: row.providerId, klarityPayerIds: [row.klarityPayerId] },
                      ])
                    : await addSoloProviderPayers([row.klarityPayerId]);
            if (res?.error) {
                message.error(res.error || 'Failed to resubmit');
                return;
            }
            message.success(`${row.name} resubmitted.`);
            setResubmitTarget(null);
            refetchPanels();
            refetchPanelSummary();
            refetchActionNeeded();
            if (isGroup) {
                refetchPracticeGroups();
                refetchPracticePanelSummary();
                refetchGroupActionNeeded();
            }
        } finally {
            setResubmitting(false);
        }
    };

    // 首次理赔前的两项准备（V3）：fax 与 payout 合并成一条提醒条，规则与 submitted 页共用同一个 hook
    const { faxMissing, payoutDone, setPayoutDone } = useCredSetupStatus(isGroup, credentialInfo.providers);
    // Settings 里改了 malpractice / 签名后横幅要跟着变：静默重拉 overview 只喂给横幅。
    // 不用 refetchCredentialInfo——它会让整页回到 Spin，把正开着的 Settings 弹窗卸载掉
    const [alertOverview, setAlertOverview] = useState<TCredentialingOverview | null>(null);
    const refreshAlertOverview = useCallback(() => {
        getCredentialingOverview()
            .then((res: any) => {
                if (!res?.error && res?.data?.data) setAlertOverview(res.data.data);
            })
            .catch(() => undefined);
    }, []);
    const [payoutModalOpen, setPayoutModalOpen] = useState(false);
    const [groupSelectedProviderId, setGroupSelectedProviderId] = useState<number | null>(null);
    const [provDropdownOpen, setProvDropdownOpen] = useState(false);
    const [provDropdownSearch, setProvDropdownSearch] = useState('');

    // providerId → 任一 panel id。all-detail 按 panel 反查 provider，同一 provider 取哪行都一样
    const panelIdByProvider = useMemo(() => {
        const result: Record<number, number> = {};
        panelRows.forEach((row) => {
            if (result[row.providerId] == null && row.id != null) result[row.providerId] = row.id;
        });
        practiceGroups.forEach((g: any) => {
            const first = g.panels?.find((p: any) => p?.id != null);
            if (g.providerId != null && result[g.providerId] == null && first) result[g.providerId] = first.id;
        });
        return result;
    }, [panelRows, practiceGroups]);

    // 打开 Edit info：拉取当前 provider 各步骤已保存的 onboarding 答案回填弹窗
    const openEditInfo = useCallback(
        (tab?: string, targetProviderId?: number, focus?: TActionFocus | null) => {
            setEditInfoValues(undefined);
            setEditInfoTab(tab);
            setEditInfoProviderId(targetProviderId);
            setEditInfoFocus(focus ?? null);
            setEditInfoOpen(true);
            const pid = targetProviderId || currentProviderId;
            fetchProviderCredValuesByPanel(panelIdByProvider[pid], pid)
                .then((vals) => {
                    setEditInfoValues(vals);
                })
                .catch(() => setEditInfoValues({}));
        },
        [currentProviderId, panelIdByProvider],
    );
    const closeEditInfo = useCallback(() => {
        setEditInfoOpen(false);
        setEditInfoValues(undefined);
        setEditInfoTab(undefined);
        setEditInfoProviderId(undefined);
        setEditInfoFocus(null);
        // Verification 里的签名是弹窗确认即保存、不走 onSaveSuccess，关闭时补一次让补签横幅跟上
        refreshAlertOverview();
    }, [refreshAlertOverview]);

    const handleViewContracts = useCallback(() => {
        openEditInfo('contracts');
    }, [openEditInfo]);

    const needSignContracts = useMemo(
        () =>
            contractBanners.filter(
                (b) => (b.status === 'SIGNATURE_PENDING' || b.status === 'SIGNATURE_NEEDED') && b.canSign,
            ),
        [contractBanners],
    );

    const notReceivedContracts = useMemo(
        () => contractBanners.filter((b) => b.status === 'SIGNATURE_NOT_RECEIVED' && b.canSign),
        [contractBanners],
    );

    const verifyingContracts = useMemo(
        () => contracts.filter((c) => c.status === 'SIGNATURE_VERIFYING'),
        [contracts],
    );

    const countersignedContracts = useMemo(
        () => contracts.filter((c) => c.status === 'SIGNATURE_AWAITING_COUNTERSIGN'),
        [contracts],
    );

    const expiredContracts = useMemo(() => contracts.filter((c) => c.status === 'SIGNATURE_LINK_EXPIRED'), [contracts]);

    const fullySignedWaitingActivation = useMemo(
        () =>
            contracts.filter(
                (c) => c.status === 'SIGNATURE_EXECUTED' && (c.files?.length ?? 0) > 0 && !c.payerEffectiveAt,
            ),
        [contracts],
    );

    const stateListTyped = stateList as SettingEnum[] | undefined;

    const addMenuRef = useRef<HTMLDivElement>(null);
    const provDropdownRef = useRef<HTMLDivElement>(null);

    const ownerProvider = uniqueProviders.find((p) => p.owner);
    // const showOwnerTabs = isGroup && !!ownerProvider;
    const showOwnerTabs = isGroup && isAdminView;

    const isGroupView = isGroup && showOwnerTabs && ownerTab === 'practice';
    const isMyCredentialing = isGroup && showOwnerTabs && ownerTab === 'my';
    const isGroupOverview = isGroupView && groupSelectedProviderId == null;
    const hasGroupSelectedProvider = isGroupView && groupSelectedProviderId != null;

    const viewActionPanels = useMemo(() => {
        if (hasGroupSelectedProvider && groupSelectedProviderId != null) {
            const provGroup = groupActionNeeded.find((g) => g.providerId === groupSelectedProviderId);
            return provGroup?.panels || [];
        }
        if (isMyCredentialing) {
            return actionNeeded;
        }
        return actionNeeded;
    }, [hasGroupSelectedProvider, groupSelectedProviderId, isMyCredentialing, actionNeeded, groupActionNeeded]);

    const handleUpdateProfile = useCallback(
        (item: TCredentialingActionItem, providerId?: number) => {
            // 字段级定位（ctaPayload 协议）：fieldKey 可解析时按它定 tab 并透传 focus，
            // 弹窗内自动进编辑并滚动标红目标字段；否则退化为按 step 只打开对应 tab
            const focus = buildActionFocus(item);
            const tab = (focus && focusTab(focus)) || (item.step ? ACTION_STEP_TO_TAB[item.step] : undefined);
            const provId =
                providerId ||
                (hasGroupSelectedProvider && groupSelectedProviderId != null ? groupSelectedProviderId : undefined);
            openEditInfo(tab, provId, focus);
        },
        [hasGroupSelectedProvider, groupSelectedProviderId, openEditInfo],
    );

    const handleFixPayment = useCallback(() => {
        setPaymentModalOpen(true);
    }, []);

    // ?action=update-payment 深链（如支付失败通知）：进页直接吊起更新支付方式弹窗。
    // 处理后立即清掉参数，避免刷新/前进后退重复弹；watch searchParams 以覆盖同路由再次带参进入（不重挂载）。
    // 注：无覆盖州 / onboarding 未完成回落 landing 的早退分支不渲染 EditPaymentModal，此时深链静默忽略
    useEffect(() => {
        if (searchParams.get('action') !== 'update-payment') return;
        setPaymentModalOpen(true);
        setSearchParams(
            (prev) => {
                prev.delete('action');
                return prev;
            },
            { replace: true },
        );
    }, [searchParams, setSearchParams]);

    const handleConfirmSignature = useCallback(
        async (_item: TCredentialingActionItem, credentialingPayerId: number) => {
            const res = await markContractSigned(credentialingPayerId);
            if (res && !res.error) {
                message.success('Contract signed status updated');
                refetchPanels();
                refetchPanelSummary();
                refetchPracticePanelSummary();
                refetchActionNeeded();
                refetchGroupActionNeeded();
                refetchPracticeGroups();
            }
        },
        [
            refetchPanels,
            refetchPanelSummary,
            refetchPracticePanelSummary,
            refetchActionNeeded,
            refetchGroupActionNeeded,
            refetchPracticeGroups,
        ],
    );

    const handleContractSigned = useCallback(() => {
        const refetchAll = () => {
            refetchContractBanner();
            refetchContracts();
            refetchPanels();
            refetchPanelSummary();
            refetchPracticePanelSummary();
            refetchActionNeeded();
            refetchGroupActionNeeded();
            refetchPracticeGroups();
        };
        let count = 0;
        const poll = () => {
            refetchAll();
            count++;
            if (count < 3) {
                setTimeout(poll, 4000);
            }
        };
        setTimeout(poll, 2000);
    }, [
        refetchContractBanner,
        refetchContracts,
        refetchPanels,
        refetchPanelSummary,
        refetchPracticePanelSummary,
        refetchActionNeeded,
        refetchGroupActionNeeded,
        refetchPracticeGroups,
    ]);

    useEffect(() => {
        if (!addMenuOpen) return undefined;
        const handler = (e: MouseEvent) => {
            if (addMenuRef.current && !addMenuRef.current.contains(e.target as Node)) {
                setAddMenuOpen(false);
            }
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [addMenuOpen]);

    useEffect(() => {
        if (!provDropdownOpen) return undefined;
        const handler = (e: MouseEvent) => {
            if (provDropdownRef.current && !provDropdownRef.current.contains(e.target as Node)) {
                setProvDropdownOpen(false);
            }
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [provDropdownOpen]);

    const geoStates = credentialInfo.states;
    const isMultiState = geoStates.length > 1;

    const viewPanels = useMemo(() => {
        if (!isGroup) return panels;
        if (isGroupOverview) return panels;
        if (isMyCredentialing && ownerProvider) {
            return providerPanelsMap[ownerProvider.providerId] || [];
        }
        if (hasGroupSelectedProvider && groupSelectedProviderId != null) {
            return providerPanelsMap[groupSelectedProviderId] || [];
        }
        return panels;
    }, [
        isGroup,
        isGroupOverview,
        isMyCredentialing,
        hasGroupSelectedProvider,
        panels,
        providerPanelsMap,
        ownerProvider,
        groupSelectedProviderId,
    ]);

    const summaryForView = useMemo(() => {
        if (isGroupOverview) return practicePanelSummary;
        return panelSummary;
    }, [isGroupOverview, panelSummary, practicePanelSummary]);

    const bucketCount = useCallback(
        (bucket: StatusBucket) => {
            const map: Record<string, keyof typeof summaryForView> = {
                action: 'actionNeeded',
                inprogress: 'inProgress',
                approved: 'approved',
            };
            const key = map[bucket];
            if (key) return summaryForView[key];
            return 0;
        },
        [summaryForView],
    );

    const effectiveActiveState = useMemo(() => {
        if (!isMultiState) return null;
        return geoStates.includes(activeState || '') ? activeState! : geoStates[0];
    }, [isMultiState, geoStates, activeState]);

    const displayRows = useMemo(() => {
        const rows = isMultiState ? viewPanels.filter((p) => p.state === effectiveActiveState) : viewPanels;
        return [...rows].sort((a, b) => BUCKET_ORDER.indexOf(a.statusBucket) - BUCKET_ORDER.indexOf(b.statusBucket));
    }, [viewPanels, isMultiState, effectiveActiveState]);

    const filterActive = statusFilters.length > 0;

    const handleStateChange = useCallback((st: string) => {
        setActiveState(st);
        setStatusFilters([]);
    }, []);

    const dropdownFilteredProviders = useMemo(() => {
        if (!provDropdownSearch) return uniqueProviders;
        const q = provDropdownSearch.toLowerCase();
        return uniqueProviders.filter(
            (p) => p.name.toLowerCase().includes(q) || p.credentials.toLowerCase().includes(q),
        );
    }, [provDropdownSearch, uniqueProviders]);

    const handleSelectProvider = useCallback((id: number | null) => {
        setGroupSelectedProviderId(id);
        setProvDropdownOpen(false);
    }, []);

    if (coverStatesMode === ECoverStatesMode.NOCOVERAGE) {
        const stateName = geoStates[0] || 'your state';
        return (
            <div className={s.statusPage}>
                <div className={s.header}>
                    <div className={s.headerRow}>
                        <div>
                            <h2 className={s.headerTitle}>
                                Credentialing
                                {feeWaived && <span className={s.feeWaivedChip}>Credentialing fee waived</span>}
                            </h2>
                            <p className={s.headerDesc}>Manage your credentialing and insurance panels.</p>
                        </div>
                        {!isProviderReadOnly && (
                            <div className={s.headerActions}>
                                <span className={s.headerBtn} onClick={() => openEditInfo()}>
                                    <SvgEdit /> Settings
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <CredentialingInfoModal
                    open={editInfoOpen}
                    onClose={closeEditInfo}
                    initialValues={editInfoValues}
                    providerId={currentProviderId}
                    practiceType={practiceType}
                    panelIdByProvider={panelIdByProvider}
                    inProgressProviderIds={inProgressProviderIds}
                />
                <div className={s.noCoverageCard}>
                    <h3 className={s.noCoverageTitle}>We&apos;re not in {stateName} yet, but we&apos;re expanding</h3>
                    <p className={s.noCoverageDesc}>
                        Klarity doesn&apos;t currently credential payers in {stateName}. Your information is saved, and
                        we&apos;ll start credentialing automatically the moment we add payers in your state.
                    </p>
                    <label className={s.noCoverageNotify}>
                        <input type="checkbox" defaultChecked />
                        Notify me when payers are available in {stateName}
                    </label>
                </div>
                <div className={s.suggestRow}>
                    <span className={s.suggestLabel}>Don&apos;t see a payer you need?</span>
                    <span className={s.suggestBtn} onClick={() => setSuggestOpen(true)}>
                        Suggest a payer
                    </span>
                </div>
                <SuggestPayerModal open={suggestOpen} onClose={() => setSuggestOpen(false)} states={geoStates} />
            </div>
        );
    }

    let emptyMsg = 'No panels yet.';
    if (filterActive) {
        emptyMsg = 'No panels match this filter.';
    } else if (isMultiState) {
        emptyMsg = `No ${effectiveActiveState} panels yet.`;
    }

    if (!credInfoLoadedRef.current || credentialInfoLoading || !credentialInfo) {
        return (
            <div
                className={s.statusPage}
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}
            >
                <Spin size="large" />
            </div>
        );
    }
    const finishOnboarding =
        credentialInfo?.currentStep === ECredentialingStep.COMPLETED ||
        credentialInfo?.currentStep === ECredentialingStep.CONFIRM;
    if (!finishOnboarding && !isTeamProvider) {
        return <PPACredentialingLandingPage isTab />;
    }

    return (
        <div className={s.statusPage}>
            {/* Header */}
            <div className={s.header}>
                <div className={s.headerRow}>
                    <div>
                        <h2 className={s.headerTitle}>
                            Credentialing
                            {feeWaived && <span className={s.feeWaivedChip}>Credentialing fee waived</span>}
                        </h2>
                        <p className={s.headerDesc}>Manage your credentialing and insurance panels.</p>
                    </div>
                    {isAdminView && (
                        <div className={s.headerActions}>
                            <span className={s.headerBtn} onClick={() => openEditInfo()}>
                                <SvgEdit /> Settings
                            </span>
                            <div className={s.addWrap} ref={addMenuRef}>
                                <span
                                    className={s.headerBtn}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setAddMenuOpen(!addMenuOpen);
                                    }}
                                >
                                    <SvgPlus /> Add
                                </span>
                                <div className={`${s.addMenu}${addMenuOpen ? ` ${s.open}` : ''}`}>
                                    {isGroup && (
                                        <div
                                            className={s.addMenuItem}
                                            onClick={() => {
                                                setAddMenuOpen(false);
                                                ///add-credentialing-provider
                                                navigate(PATH.ADD_CREDENTIALING_PROVIDER);
                                            }}
                                        >
                                            <SvgPlus /> Add provider
                                        </div>
                                    )}
                                    <div
                                        className={s.addMenuItem}
                                        onClick={() => {
                                            setAddMenuOpen(false);
                                            setAddPayersOpen(true);
                                        }}
                                    >
                                        <SvgPlus /> Add payers
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* fax / payout 准备提醒（不可关闭；补齐后自然消失） */}
            {!isProviderReadOnly && (
                <SetupReminderBanner
                    faxMissing={faxMissing}
                    payoutMissing={!payoutDone}
                    onSetupPayout={() => setPayoutModalOpen(true)}
                />
            )}
            {!isProviderReadOnly && (
                <SignatureBackfillBanner
                    overview={alertOverview ?? credentialInfo}
                    currentProviderId={currentProviderId}
                    isGroup={isGroup}
                    onChanged={refreshAlertOverview}
                />
            )}
            {!isProviderReadOnly && (
                <MalpracticeExpiryBanner
                    overview={alertOverview ?? credentialInfo}
                    currentProviderId={currentProviderId}
                    onUpdate={(providerId) => openEditInfo('malpractice', providerId)}
                />
            )}

            {/* Owner tabs (group only when owner is also provider) */}
            {showOwnerTabs && (
                <div className={s.ownerTabs}>
                    <span
                        className={`${s.ownerTab}${ownerTab === 'practice' ? ` ${s.active}` : ''}`}
                        onClick={() => {
                            setOwnerTab('practice');
                            setGroupSelectedProviderId(null);
                        }}
                    >
                        Practice view
                    </span>
                    <span
                        className={`${s.ownerTab}${ownerTab === 'my' ? ` ${s.active}` : ''}`}
                        onClick={() => setOwnerTab('my')}
                    >
                        My credentialing
                    </span>
                </div>
            )}

            {/* Growth banner removed per 07/22 design update (#12) */}
            {/* Practice view (group owner, practice tab) */}
            {isGroupView && (
                <div className={s.groupWrap}>
                    {isGroupOverview ? (
                        <GroupActionNeededBanner
                            groupActionNeeded={groupActionNeeded}
                            onUpdateProfile={handleUpdateProfile}
                            onFixPayment={handleFixPayment}
                            onConfirmSignature={handleConfirmSignature}
                        />
                    ) : (
                        <ActionNeededBanner
                            actionNeededPanels={viewActionPanels}
                            onUpdateProfile={handleUpdateProfile}
                            onFixPayment={handleFixPayment}
                            onConfirmSignature={handleConfirmSignature}
                        />
                    )}
                    <SignContractsBanner
                        contracts={needSignContracts}
                        stateList={stateList as SettingEnum[]}
                        isGroup
                        onSigned={handleContractSigned}
                    />
                    <CountersignedBanner
                        verifyingContracts={verifyingContracts}
                        countersignedContracts={countersignedContracts}
                        fullySignedContracts={fullySignedWaitingActivation}
                        stateList={stateListTyped}
                        onViewContracts={handleViewContracts}
                    />
                    <ExpiredBanner
                        contracts={expiredContracts}
                        stateList={stateListTyped}
                    />
                    <NotReceivedBanner
                        contracts={notReceivedContracts}
                        stateList={stateList as SettingEnum[]}
                        onSigned={handleContractSigned}
                    />
                    {hasGroupSelectedProvider && (
                        <ProviderBar
                            providers={uniqueProviders}
                            groupSelectedProviderId={groupSelectedProviderId}
                            provDropdownOpen={provDropdownOpen}
                            provDropdownSearch={provDropdownSearch}
                            dropdownFilteredProviders={dropdownFilteredProviders}
                            provDropdownRef={provDropdownRef}
                            onSelectProvider={handleSelectProvider}
                            onToggleDropdown={() => {
                                setProvDropdownOpen(!provDropdownOpen);
                                setProvDropdownSearch('');
                            }}
                            onDropdownSearchChange={setProvDropdownSearch}
                        />
                    )}
                    <div className={s.summaryBar}>
                        <SummaryCard
                            label="Action needed"
                            num={bucketCount('action')}
                            tip="Something you need to do is blocking progress: a deposit, a missing or invalid document, or a payer request for information (RFI)."
                        />
                        <SummaryCard
                            label="In progress"
                            num={bucketCount('inprogress')}
                            tip="We're moving your application forward. Klarity is reviewing your packet, or the payer is reviewing it. No action unless we ask."
                        />
                        <SummaryCard
                            label="Approved"
                            num={bucketCount('approved')}
                            tip="The payer has approved you, with effective date and contract confirmed. Billing set-up comes next. Being approved isn't the same as billing being active."
                        />
                    </div>
                    {isMultiState && !isGroupOverview && (
                        <div className={s.stateChips}>
                            {geoStates.map((st) => {
                                const cnt = viewPanels.filter((p) => p.state === st).length;
                                return (
                                    <span
                                        key={st}
                                        className={`${s.stateChip}${st === effectiveActiveState ? ` ${s.active}` : ''}`}
                                        onClick={() => handleStateChange(st)}
                                    >
                                        {st} <span className={s.stateChipCount}>{cnt}</span>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    {isGroupOverview ? (
                        <GroupCredentialingRecordTable
                            practiceGroups={practiceGroups}
                            currentProviderId={currentProviderId}
                            isMultiState={isMultiState}
                            onWithdraw={isAdminView ? setWithdrawTarget : undefined}
                            onResubmit={isAdminView ? setResubmitTarget : undefined}
                        />
                    ) : (
                        <SoloCredentialingRecordTable
                            displayRows={displayRows}
                            emptyMsg={emptyMsg}
                            statusFilters={statusFilters}
                            isMultiState={isMultiState}
                            onStatusFilterChange={setStatusFilters}
                            onWithdraw={isAdminView ? setWithdrawTarget : undefined}
                            onResubmit={isAdminView ? setResubmitTarget : undefined}
                        />
                    )}
                </div>
            )}

            {/* My credentialing (group owner, my tab) */}
            {isMyCredentialing && (
                <div className={s.meWrap}>
                    <ActionNeededBanner
                        actionNeededPanels={viewActionPanels}
                        onUpdateProfile={handleUpdateProfile}
                        onFixPayment={handleFixPayment}
                        onConfirmSignature={handleConfirmSignature}
                    />
                    <div className={s.summaryBar}>
                        <SummaryCard
                            label="Action needed"
                            num={bucketCount('action')}
                            tip="Something you need to do is blocking progress: a deposit, a missing or invalid document, or a payer request for information (RFI)."
                        />
                        <SummaryCard
                            label="In progress"
                            num={bucketCount('inprogress')}
                            tip="We're moving your application forward. Klarity is reviewing your packet, or the payer is reviewing it. No action unless we ask."
                        />
                        <SummaryCard
                            label="Approved"
                            num={bucketCount('approved')}
                            tip="The payer has approved you, with effective date and contract confirmed. Billing set-up comes next. Being approved isn't the same as billing being active."
                        />
                    </div>
                    {isMultiState && (
                        <div className={s.stateChips}>
                            {geoStates.map((st) => {
                                const cnt = viewPanels.filter((p) => p.state === st).length;
                                return (
                                    <span
                                        key={st}
                                        className={`${s.stateChip}${st === effectiveActiveState ? ` ${s.active}` : ''}`}
                                        onClick={() => handleStateChange(st)}
                                    >
                                        {st} <span className={s.stateChipCount}>{cnt}</span>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    <SoloCredentialingRecordTable
                        displayRows={displayRows}
                        emptyMsg={emptyMsg}
                        statusFilters={statusFilters}
                        isMultiState={isMultiState}
                        onStatusFilterChange={setStatusFilters}
                        onWithdraw={isAdminView ? setWithdrawTarget : undefined}
                        onResubmit={isAdminView ? setResubmitTarget : undefined}
                    />
                </div>
            )}

            {/* Solo view (no group tabs) */}
            {!showOwnerTabs && (
                <div className={s.meWrap}>
                    {!isProviderReadOnly && (
                        <>
                            <ActionNeededBanner
                                actionNeededPanels={viewActionPanels}
                                onUpdateProfile={handleUpdateProfile}
                                onFixPayment={handleFixPayment}
                                onConfirmSignature={handleConfirmSignature}
                            />
                            <SignContractsBanner
                                contracts={needSignContracts}
                                stateList={stateList as SettingEnum[]}
                                onSigned={handleContractSigned}
                            />
                            <CountersignedBanner
                                verifyingContracts={verifyingContracts}
                                countersignedContracts={countersignedContracts}
                                fullySignedContracts={fullySignedWaitingActivation}
                                stateList={stateListTyped}
                                onViewContracts={handleViewContracts}
                            />
                            <ExpiredBanner contracts={expiredContracts} stateList={stateListTyped} />
                            <NotReceivedBanner
                                contracts={notReceivedContracts}
                                stateList={stateList as SettingEnum[]}
                                onSigned={handleContractSigned}
                            />
                        </>
                    )}
                    <div className={s.summaryBar}>
                        <SummaryCard
                            label="Action needed"
                            num={bucketCount('action')}
                            tip="Something you need to do is blocking progress: a deposit, a missing or invalid document, or a payer request for information (RFI)."
                        />
                        <SummaryCard
                            label="In progress"
                            num={bucketCount('inprogress')}
                            tip="We're moving your application forward. Klarity is reviewing your packet, or the payer is reviewing it. No action unless we ask."
                        />
                        <SummaryCard
                            label="Approved"
                            num={bucketCount('approved')}
                            tip="The payer has approved you, with effective date and contract confirmed. Billing set-up comes next. Being approved isn't the same as billing being active."
                        />
                    </div>
                    {isMultiState && (
                        <div className={s.stateChips}>
                            {geoStates.map((st) => {
                                const cnt = viewPanels.filter((p) => p.state === st).length;
                                return (
                                    <span
                                        key={st}
                                        className={`${s.stateChip}${st === effectiveActiveState ? ` ${s.active}` : ''}`}
                                        onClick={() => handleStateChange(st)}
                                    >
                                        {st} <span className={s.stateChipCount}>{cnt}</span>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    <SoloCredentialingRecordTable
                        displayRows={displayRows}
                        emptyMsg={emptyMsg}
                        statusFilters={statusFilters}
                        isMultiState={isMultiState}
                        onStatusFilterChange={setStatusFilters}
                        onWithdraw={isAdminView ? setWithdrawTarget : undefined}
                        onResubmit={isAdminView ? setResubmitTarget : undefined}
                    />
                </div>
            )}

            <WithdrawPayerModal
                target={withdrawTarget}
                loading={withdrawing}
                onCancel={() => setWithdrawTarget(null)}
                onConfirm={handleWithdrawConfirm}
            />

            <ResubmitPayerModal
                target={resubmitTarget}
                loading={resubmitting}
                onCancel={() => setResubmitTarget(null)}
                onConfirm={handleResubmitConfirm}
            />

            {/* Cross-sell */}
            {!isProviderReadOnly && (
                <SpeedUpModule userPlatformStatus={userPlatformStatus} isGroup={isGroup} homeInfo={providerInfo} />
            )}

            {/* Upcoming payers */}
            {upcomingPayers.length > 0 && (
                <div className={s.upcoming}>
                    <p className={s.upcomingTitle}>Upcoming</p>
                    <p className={s.upcomingDesc}>
                        Additional payers are added regularly. You will be notified when new options become available in
                        your state.
                    </p>
                    <div className={s.logoWall}>
                        {upcomingPayers.map((p) => (
                            <div key={p.id} className={s.logoTile}>
                                <span className={s.logoName}>{p.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Suggest a payer */}
            {!isProviderReadOnly && (
                <div className={s.suggestRow}>
                    <span className={s.suggestLabel}>Don&apos;t see a payer you need?</span>
                    <span className={s.suggestBtn} onClick={() => setSuggestOpen(true)}>
                        Suggest a payer
                    </span>
                </div>
            )}

            {/* Other states link */}
            <p className={s.otherStates}>
                Practicing in more than one state? Klarity also credentials payers in other states.{' '}
                <a
                    href={`${getHelloKlarityHost()}/insurance-credentialing#:~:text=Payers%20available%20in,Superior%20HealthPlan`}
                    target="_blank"
                    rel="noreferrer"
                >
                    See payers in other states &rarr;
                </a>
            </p>

            <SuggestPayerModal open={suggestOpen} onClose={() => setSuggestOpen(false)} states={geoStates} />
            <CredentialingModal
                open={addPayersOpen}
                onClose={() => setAddPayersOpen(false)}
                flowMode={ECredentialingFlowMode.ADD}
                onEnd={() => {
                    refetchPanels();
                    refetchPracticeGroups();
                    refetchCredentialInfo();
                    refetchPanelSummary();
                    refetchPracticePanelSummary();
                }}
            />
            <CredentialingInfoModal
                open={editInfoOpen}
                onClose={closeEditInfo}
                onSaveSuccess={() => {
                    refreshAlertOverview();
                    refetchActionNeeded();
                    refetchGroupActionNeeded();
                    refetchPanels();
                    refetchPanelSummary();
                    refetchPracticePanelSummary();
                }}
                initialValues={editInfoValues}
                providerId={currentProviderId}
                practiceType={practiceType}
                initialTab={editInfoTab}
                initialFocus={editInfoFocus}
                initialProviderId={editInfoProviderId}
                panelIdByProvider={panelIdByProvider}
                inProgressProviderIds={inProgressProviderIds}
            />
            <EditPaymentModal
                open={paymentModalOpen}
                onClose={() => setPaymentModalOpen(false)}
                onUpdatePaymentInfo={() => {
                    setPaymentModalOpen(false);
                    refetchActionNeeded();
                    refetchGroupActionNeeded();
                    refetchPanels();
                    refetchPanelSummary();
                    refetchPracticePanelSummary();
                }}
            />
            <CredentialingPayoutModal
                open={payoutModalOpen}
                onClose={() => setPayoutModalOpen(false)}
                onSaved={() => setPayoutDone(true)}
            />
        </div>
    );
};

// Group provider 没有自己的 provider 记录时（invite link 加入，token 是 DELEGATED 到 owner），
// 后端会拒绝 credentialing 接口，这里直接给提示，不发请求。
const CredentialingPage = () => {
    const [currentUser] = useCurrentUser();
    if (isDelegatedGroupProvider(currentUser)) {
        return (
            <div className={s.statusPage}>
                <div className={s.header}>
                    <h2 className={s.headerTitle}>Credentialing</h2>
                </div>
                <div className={s.noCoverageCard}>
                    <h3 className={s.noCoverageTitle}>No credentialing record for your account yet</h3>
                    <p className={s.noCoverageDesc}>
                        Your practice owner manages credentialing. Once you have your own provider profile, your panel
                        status will show here.
                    </p>
                </div>
            </div>
        );
    }
    return <CredentialingPageContent />;
};

export default CredentialingPage;
